import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import DragDropListsNew from '../../../packages/common/shared-ui/src/components/DragDropListsNew/DragDropListsNew';
import CheckboxIcon from '../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../packages/common/shared-ui/src/icons/DeleteIcon';
import { sortOptions } from '../../../packages/common/utils/fieldHelper';
import { useTranslation } from '../../../packages/common/utils/getLocalString';

// Original class: modalRoot
const StyledModalRoot = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: '84px 0 20px 0',
  },

  '& .MuiPaper-root.MuiDialog-paper': {
    overflow: 'visible',
    padding: '20px',
    maxWidth: '600px',
    backgroundColor: '#F1F2F8',
    borderRadius: '16px',
    margin: 0,
    maxHeight: '100%',
  },

  modalPaper: {
    padding: theme.spacing(2.5, 5),
    overflowY: 'auto',
  },
}));
// Original class: blockWrapper
const StyledBlockWrapper = styled(Grid)(theme => ({
  flexWrap: 'nowrap!important',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    height: '6px',
    width: '6px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
// Original class: filterAutocomplete
const StyledFilterAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})(({ theme, multipleStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      marginRight: '5px',
      position: 'static',
      height: '10px !important',
    },
  },
}));
const GridFiltersModalNew = ({
  open,
  data,
  handleSave,
  handleClose,
  agile,
}) => {
  const getLocalString = useTranslation();
  const [savedParams, setSavedParams] = useState(null);
  const [currentParams, setCurrentParams] = useState(null);
  const removeCheckbox = useRef([
    '/finances',
    '/projects',
    '/agile',
    '/timesheets',
  ]);

  useEffect(() => {
    if (data) {
      if (removeCheckbox.current.includes(location.pathname)) {
        delete data.sorts;
      }
      setSavedParams(data);
      setCurrentParams(data);
    }
  }, [data]);

  const handleSaveButton = useCallback(() => {
    handleSave(currentParams);
  }, [currentParams, handleSave]);

  const handleClearButton = useCallback(() => {
    setCurrentParams(savedParams);
  }, [savedParams]);

  const handleCloseButton = useCallback(() => {
    setCurrentParams(savedParams);
    handleClose();
  }, [handleClose, savedParams]);

  const handleField = useCallback(
    (value, field) => {
      let currentValue = value;
      if (field.key === 'treeView') {
        currentValue = currentValue.toString();
      }
      setCurrentParams(prev => ({
        ...currentParams,
        [field.key]: { ...field.value, selectedItems: currentValue },
      }));
    },
    [currentParams],
  );

  return (
    <StyledModalRoot open={open} disableRestoreFocus fullWidth={true}>
      <Grid
        container
        wrap='nowrap'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Typography variant='h1'>
          {getLocalString('ParamsPopup', 'title')}
        </Typography>
        <IconButton disableRipple value='close' onClick={handleCloseButton}>
          <Box
            sx={{
              width: 24,
              height: 24,
            }}
          >
            <ClearIcon width={24} height={24} viewBox='0 0 24 24' />
          </Box>
        </IconButton>
      </Grid>
      <StyledBlockWrapper
        container
        direction='column'
        sx={{
          flexWrap: 'nowrap',
        }}
      >
        {currentParams &&
          Object.keys(currentParams)
            .map(key => ({ key, value: currentParams[key] }))
            .filter(item => item.key !== 'fields')
            .map((item, index) => {
              return (
                <Grid
                  container
                  key={index}
                  sx={{
                    marginTop: index > 0 ? '20px' : 0,
                    alignItems: 'center',
                  }}
                >
                  {item.value.type !== 'columns' && (
                    <Grid
                      sx={{
                        width: '200px',
                      }}
                    >
                      <Typography variant='h5'>
                        {agile && item.key === 'groups'
                          ? getLocalString('AgileContainer', 'labels', 'sorts')
                          : getLocalString('ParamsPopup', 'labels', item.key)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid
                    sx={{
                      flex: 1,
                    }}
                  >
                    {item.value.type === 'list' && item.value.multiple && (
                      <StyledFilterAutocomplete
                        multipleStyle={true}
                        forcePopupIcon={false}
                        sx={{
                          width: item.key === 'groups' ? '253px' : '352px',
                        }}
                        multiple
                        disableClearable
                        onChange={(event, newValue) =>
                          handleField(newValue, item)
                        }
                        options={sortOptions(item.value.items, item)}
                        disableCloseOnSelect
                        disablePortal
                        value={
                          item.value.selectedItems
                            ? item.value.selectedItems
                            : []
                        }
                        getOptionLabel={option => option.fieldName}
                        renderOption={(props, option, { selected }) => (
                          <li
                            {...props}
                            key={option.fieldId}
                            aria-disabled={option.fieldType === 'base_plan'}
                          >
                            <Checkbox
                              icon=<CheckboxIcon
                                width='12'
                                height='12'
                                viewBox='0 0 12 12'
                              />
                              checkedIcon=<CheckboxIconChecked
                                width='12'
                                height='12'
                                viewBox='0 0 12 12'
                              />
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.fieldName}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <StyledChip
                              {...getTagProps({ index })}
                              disabled={option.fieldType === 'base_plan'}
                              key={index}
                              deleteIcon={
                                <DeleteIcon
                                  width={10}
                                  height={10}
                                  viewBox='0 0 10 10'
                                />
                              }
                              variant='outlined'
                              label={option.fieldName}
                            />
                          ))
                        }
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder={getLocalString('labels', 'input')}
                          />
                        )}
                      />
                    )}
                    {item.value.type === 'list' && !item.value.multiple && (
                      <StyledFilterAutocomplete
                        popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                        sx={{
                          width: item.key === 'groups' ? '253px' : '352px',
                        }}
                        onChange={(event, newValue) =>
                          handleField(newValue, item)
                        }
                        options={item.value.items}
                        disablePortal
                        value={
                          item.value.selectedItems
                            ? item.value.selectedItems
                            : ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        getOptionLabel={option => {
                          if (option) {
                            const optionValue = Array.isArray(option)
                              ? option[0]
                              : option;
                            return optionValue.fieldName;
                          } else {
                            return '';
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder={getLocalString('labels', 'input')}
                          />
                        )}
                        renderOption={(props, option) => {
                          return (
                            <MenuItem
                              {...props}
                              selected={
                                option.fieldId ===
                                item.value?.selectedItems?.fieldId
                              }
                              key={option.fieldId + option.fieldName}
                              value={option.fieldId}
                            >
                              {option.fieldName}
                            </MenuItem>
                          );
                        }}
                      />
                    )}
                    {item.value.type === 'checkbox' && (
                      <Checkbox
                        checked={item.value.selectedItems === 'true'}
                        disableRipple
                        onChange={(event, newValue) =>
                          handleField(event.target.checked, item)
                        }
                        icon={
                          <CheckboxIcon
                            width='12'
                            height='12'
                            viewBox='0 0 12 12'
                          />
                        }
                        checkedIcon={
                          <CheckboxIconChecked
                            width='12'
                            height='12'
                            viewBox='0 0 12 12'
                          />
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              );
            })}
        {currentParams &&
          Object.keys(currentParams)
            .map(key => ({ key, value: data[key] }))
            .filter(item => item.key === 'fields')
            .map((item, index) => {
              return (
                <DragDropListsNew
                  key={index}
                  fields={item.value.selectedItems}
                  handleChange={data => handleField(data, item)}
                  getOptionLabel={option =>
                    `${option.fieldId};${option.fieldName}`
                  }
                />
              );
            })}
      </StyledBlockWrapper>
      <Grid
        container
        sx={{
          justifyContent: 'center',
          marginTop: 5,
        }}
      >
        <Button
          disableRipple
          variant='cutGreyPrimaryBig'
          onClick={handleClearButton}
        >
          <Typography
            variant='h5'
            sx={{
              fontWeight: 700,
            }}
          >
            {getLocalString('buttons', 'clear')}
          </Typography>
        </Button>
        <Box
          sx={{
            marginLeft: 1,
          }}
        >
          <Button
            disableRipple
            variant='cutBlueSecondary'
            onClick={handleSaveButton}
          >
            <Typography
              variant='h5'
              sx={{
                fontWeight: 700,
              }}
            >
              {getLocalString('buttons', 'save')}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </StyledModalRoot>
  );
};

export default GridFiltersModalNew;
